/*
 * Назначение: общие утилиты и хуки для вкладок настроек проекта.
 * Функционал:
 * - splitLines: построчная нарезка textarea-полей в массив (тримит, фильтрует пустые);
 * - useProjectDetails: загрузка деталей текущего проекта через TanStack Query;
 * - useProjectMutation: единая обёртка над PATCH /api/admin/projects/current,
 *   с тостами успеха/ошибки и инвалидцией кэша.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { projectsApi } from "../../../lib/admin-api";
import { ApiError } from "../../../lib/api";
import { useProject } from "../../components/project/project-provider";
import type { ProjectUpdate } from "../../../lib/types";

/** Разбить значение textarea на массив строк, без пустых. */
export const splitLines = (s: string): string[] =>
  s
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean);

/** Подгрузка деталей проекта (включая тему виджета, промпт и пр.). */
export function useProjectDetails() {
  const { currentSlug } = useProject();
  return useQuery({
    queryKey: ["project", currentSlug, "details"],
    queryFn: () => projectsApi.current(),
    enabled: !!currentSlug,
  });
}

/**
 * Обёртка над PATCH-обновлением проекта. Все вкладки делятся одной мутацией —
 * тосты, обновление кэша и сообщения об ошибках одинаковы во всём разделе.
 */
export function useProjectMutation(action: string) {
  const { currentSlug, reloadProjects } = useProject();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProjectUpdate) => projectsApi.update(payload),
    onSuccess: (project, payload) => {
      toast.success("Сохранено");
      queryClient.setQueryData(["project", currentSlug, "details"], project);
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      // Переименовали проект — обновляем переключатель проектов в сайдбаре.
      if ("name" in payload) reloadProjects();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.detail : `Не удалось ${action}`);
    },
  });
}
